import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Truck, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';
import PhotoUpload from '@/components/PhotoUpload';

const QuoteMiniMove = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const suburb = searchParams.get('suburb') || '';
  const campaign = searchParams.get('campaign') || '';
  const referralCode = searchParams.get('ref') || '';
  
  const [formData, setFormData] = useState({
    pickup: suburb,
    dropoff: '',
    items: '',
    floors: '',
    helpers: 'driver_only',
    datetime: '',
    name: '',
    mobile: '',
    email: '',
    photos: [],
    referral_code: referralCode
  });

  const handlePhotosChange = (photos) => {
    setFormData({ ...formData, photos });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sessionStorage.setItem('quoteData', JSON.stringify({
      ...formData,
      serviceType: 'mini-move',
      campaign
    }));
    navigate('/quote/confirm');
  };

  const whatsappLink = generateWhatsAppLink('miniMove', {
    pickup: formData.pickup,
    dropoff: formData.dropoff,
    items: formData.items,
    helpers: formData.helpers,
    datetime: formData.datetime
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quote-mini-move-page">
      {/* Progress Indicator */}
      <div className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">1</div>
              <span className="ml-2 text-sm font-medium text-[#039fff]">Details</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">2</div>
              <span className="ml-2 text-sm text-gray-600">Confirm</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-gray-600">Done</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <Truck className="h-12 w-12 text-[#039fff] mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-[#001A47] text-center mb-2">Mini-Move Quote</h1>
              <p className="text-center text-gray-600">Tell us about your mini-move</p>
              {referralCode && (
                <div className="mt-3 text-center">
                  <span className="inline-flex items-center bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                    Referral code applied: {referralCode}
                  </span>
                </div>
              )}
            </div>

            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickup">Pickup Location *</Label>
                      <Input
                        id="pickup"
                        data-testid="pickup-input"
                        value={formData.pickup}
                        onChange={(e) => setFormData({ ...formData, pickup: e.target.value })}
                        placeholder="e.g., Stellenbosch"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="dropoff">Drop-off Location *</Label>
                      <Input
                        id="dropoff"
                        data-testid="dropoff-input"
                        value={formData.dropoff}
                        onChange={(e) => setFormData({ ...formData, dropoff: e.target.value })}
                        placeholder="e.g., Cape Town CBD"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="items">Item List *</Label>
                    <Textarea
                      id="items"
                      data-testid="items-input"
                      value={formData.items}
                      onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                      placeholder="List furniture, appliances, boxes..."
                      rows={4}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Be as detailed as possible for an accurate quote</p>
                  </div>

                  {/* Photo Upload Section */}
                  <div>
                    <Label className="mb-2 block">Photos of Items (Optional)</Label>
                    <PhotoUpload 
                      onPhotosChange={handlePhotosChange} 
                      maxPhotos={5}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="floors">Floors/Lift Access *</Label>
                      <Input
                        id="floors"
                        data-testid="floors-input"
                        value={formData.floors}
                        onChange={(e) => setFormData({ ...formData, floors: e.target.value })}
                        placeholder="e.g., 2nd floor, no lift"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="helpers">Helpers Needed *</Label>
                      <select
                        id="helpers"
                        data-testid="helpers-select"
                        value={formData.helpers}
                        onChange={(e) => setFormData({ ...formData, helpers: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      >
                        <option value="driver_only">Driver only</option>
                        <option value="plus_one">Driver + 1 helper</option>
                        <option value="plus_two">Driver + 2 helpers</option>
                        <option value="unsure">Not sure (help me decide)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="datetime">Preferred Date/Time *</Label>
                    <Input
                      id="datetime"
                      data-testid="datetime-input"
                      type="datetime-local"
                      value={formData.datetime}
                      onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
                      required
                    />
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        data-testid="name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input
                        id="mobile"
                        data-testid="mobile-input"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        placeholder="+27"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  {/* Referral Code Input (if not pre-filled) */}
                  {!referralCode && (
                    <div>
                      <Label htmlFor="referral">Referral Code (Optional)</Label>
                      <Input
                        id="referral"
                        data-testid="referral-input"
                        value={formData.referral_code}
                        onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                        placeholder="e.g., REF-ABC123"
                        className="uppercase"
                      />
                      <p className="text-xs text-gray-500 mt-1">Have a friend&apos;s referral code? Enter it for a discount!</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]"
                      data-testid="continue-btn"
                    >
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button type="button" variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Send on WhatsApp
                      </Button>
                    </a>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteMiniMove;
